/**
 * Browser helpers for the header-bell notification chime and mute flag.
 *
 * Mute is sound-only: the inbox, live updates, and email are unaffected.
 * Rapid pushes are digested into a single chime per burst.
 */

const STORAGE_KEY = "orderlink.notificationSoundMuted";
const CHIME_URL = "/media/sounds/notification-chime.mp3";

/** Collapse bursts: one chime covers all arrivals within this window. */
const CHIME_DIGEST_MS = 2500;

/** @type {HTMLAudioElement|null} */
let chimeAudio = null;

/** @type {number} */
let lastChimeAt = 0;

/**
 * @return {boolean} Whether notification sound is muted.
 */
export function isNotificationSoundMuted() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch (error) {
    return false;
  }
}

/**
 * Persist the sound mute flag.
 *
 * @param {boolean} muted
 */
export function setNotificationSoundMuted(muted) {
  try {
    window.localStorage.setItem(STORAGE_KEY, muted ? "true" : "false");
  } catch (error) {
    // Ignore storage failures (private mode, etc.).
  }
}

/**
 * Play the soft notification chime when sound is not muted.
 *
 * Multiple notifications in a short burst share one chime (digest).
 * Autoplay blocks and missing files fail silently.
 */
export function playNotificationChime() {
  if (isNotificationSoundMuted()) {
    return;
  }
  const now = Date.now();
  if (now - lastChimeAt < CHIME_DIGEST_MS) {
    return;
  }
  lastChimeAt = now;
  try {
    if (!chimeAudio) {
      chimeAudio = new Audio(CHIME_URL);
    }
    chimeAudio.currentTime = 0;
    const playResult = chimeAudio.play();
    if (playResult && typeof playResult.catch === "function") {
      playResult.catch(() => {});
    }
  } catch (error) {
    // Ignore playback errors.
  }
}
